import { rem } from "@certn/cascadia/utils";
import {
  CascadiaColor,
  CascadiaShadow,
  CascadiaShape,
  CascadiaStandardIcon,
  CascadiaStyle,
  shapes,
} from "@certn/cascadia-tokens";
import { css, cx } from "@emotion/css";
import { ButtonProps as AriaButtonProps, Button } from "react-aria-components";

import { ICON_BUTTON_SIZES } from "./constants";
import { ButtonSize } from "./types";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { useColors } from "../../hooks/useColors";
import { Loader } from "../loaders/loading/Loader";
import { accessibleTouchSize, useGetAllFocusShadows } from "../utils";
export interface IconButtonProps extends AriaButtonProps {
  border?: CascadiaColor;
  color?: CascadiaColor;
  fill?: CascadiaColor;
  iconFill?: boolean;
  focusStyle?: CascadiaStyle;
  hoverColor?: CascadiaColor;
  hoverFill?: CascadiaColor;
  shadow?: CascadiaShadow;
  shape?: CascadiaShape;
  size?: ButtonSize;
  icon: CascadiaStandardIcon;
  isLoading?: boolean;
  loaderColor?: CascadiaColor;
  testID?: string;
  hasDropShadow?: boolean;
  className?: string;
}

export function _IconButton(props: IconButtonProps) {
  const {
    size = "small",
    testID = "icon-button",
    border,
    color,
    fill,
    iconFill,
    hoverColor,
    hoverFill,
    shape = "border-radius-2",
    isLoading,
    hasDropShadow,
    className,
    ...buttonProps
  } = props;
  const sizeTokens = ICON_BUTTON_SIZES[size];
  const colors = useColors();
  const getAllFocusShadows = useGetAllFocusShadows();
  const boxShadow = border ? `inset 0 0 0 1px ${colors[border]}` : undefined;

  return (
    <Button
      className={cx(
        styles.button,
        styles.colors,
        css(
          getIconButtonsize(props.size),
          isLoading && {
            "span, > i": { color: "transparent", fill: "transparent" },
          },
          {
            border: "none",
            background: fill ? colors[fill] : "transparent",
            color: color ? colors[color] : undefined,
            borderRadius: shapes[shape],
            "&:focus-within": getAllFocusShadows(boxShadow),
            boxShadow: hasDropShadow ? "var(--shadow-top-bar)" : boxShadow,
            "&:hover:enabled": {
              "> span": {
                color: hoverColor ? colors[hoverColor] : "undefined",
              },
              background: hoverFill ? colors[hoverFill] : "undefined",
            },
            "&:disabled": {
              opacity: isLoading ? 1 : 0.6,
              cursor: isLoading ? "progress" : "not-allowed",
            },
          }
        ),
        className
      )}
      data-testid={testID}
      {...buttonProps}
    >
      <Icon
        type="standard"
        name={props.icon || "close"}
        color={props.color}
        size={sizeTokens.iconSize}
        fill={props.iconFill}
      />
      {props.isLoading && (
        <Loader
          overlay
          size="small"
          background={props.fill}
          color={props.loaderColor || props.color}
        />
      )}
    </Button>
  );
}

const styles = {
  button: css({
    position: "relative",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:after": accessibleTouchSize,
  }),
  colors: css({
    border: "none",
    outline: "none",
    overflow: "hidden",
  }),
};

const getIconButtonsize = (size: ButtonSize = "medium") => {
  const iconSizeTokens = ICON_BUTTON_SIZES[size];
  return {
    width: rem(iconSizeTokens.buttonSize),
    height: rem(iconSizeTokens.buttonSize),
    justifyContent: "center",
  };
};
